import { create } from "zustand";
import axios from "axios";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";
import { useUsers } from "./useUsers";

interface IWithdraw {
  _id: string;
  user:
    | string
    | {
        _id: string;
        first_name?: string;
        last_name?: string;
        user_role?: number;
      };
  amount: number;
  withdrawType?: "community" | "personal";
  withdrawUrl: string;
  withdrawQr: string;
  cashbackId: string;
  status?: "CREATED" | "REDEEMED" | "EXPIRED" | "REFUNDED";
  isDonation?: boolean;
  community?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface IWalletStats {
  usersWithMoney: number;
  totalPendingAmount: number;
  averageHoldingTimeDays: number;
}

interface IStore {
  /** State */
  allWithdraws: IWithdraw[];
  withdraws: IWithdraw[];
  hasNextPage: boolean;
  withdraw: IWithdraw | null;
  walletStats: IWalletStats | null;
  /** Loaders */
  isWithdrawsLoading: boolean;
  isWithdrawLoading: boolean;
  isWalletStatsLoading: boolean;
  /** Callbacks */
  getAllWithdraws: (
    page?: number,
    limit?: number,
    status?: string,
    day?: number,
    month?: number,
    year?: number
  ) => void;
  getWithdraws: (userId: string, page?: number, limit?: number) => void;
  getWithdraw: (withdrawId: string) => void;
  refundWithdraw: (withdrawId: string) => void;
  getWalletStats: () => void;
  downloadWalletReport: (fromYMD: string, toYMD: string) => Promise<void>;
}

export const useWithdraws = create<IStore>()((set) => ({
  /** State */
  allWithdraws: [],
  withdraws: [],
  hasNextPage: true,
  withdraw: null,
  walletStats: null,
  /** Loaders */
  isWithdrawsLoading: false,
  isWithdrawLoading: false,
  isWalletStatsLoading: false,
  /** Callbacks */
  getAllWithdraws: async (page = 1, limit = 10, status, day, month, year) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      let url = `withdraws?page=${page}&limit=${limit}`;
      if (status) {
        url += `&status=${status}`;
      }
      if (day) {
        url += `&day=${day}`;
      }
      if (month) {
        url += `&month=${month}`;
      }
      if (year) {
        url += `&year=${year}`;
      }
      const response = await axios.get(url);
      if (response?.data) {
        set((state) => ({
          ...state,
          allWithdraws:
            page === 1
              ? response?.data
              : [...state.allWithdraws, ...response?.data],
          hasNextPage: response?.data?.length === limit,
        }));
      }
    } catch (error) {
      LOG.error("Error on getting all withdraws", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getWithdraws: async (userId, page = 1, limit = 10) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `withdraws/user/${userId}?page=${page}&limit=${limit}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          withdraws:
            page === 1
              ? response?.data
              : [...state.withdraws, ...response?.data],
          hasNextPage: response?.data?.length === limit,
        }));
      }
    } catch (error) {
      LOG.error("Error on getting withdraws", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getWithdraw: async (withdrawId: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`withdraws/user/${withdrawId}`);
      if (response?.data) {
        set({ withdraw: response?.data });
      }
    } catch (error) {
      LOG.error("Error on getting withdraw", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  refundWithdraw: async (withdrawId: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post(`withdraws/refund/${withdrawId}`);
      if (response?.data) {
        set((state) => ({
          ...state,
          allWithdraws: state.allWithdraws.filter(
            (withdraw) => withdraw._id !== withdrawId
          ),
          withdraws: state.withdraws.filter(
            (withdraw) => withdraw._id !== withdrawId
          ),
        }));
        useUsers.getState().getOneUser(response?.data?.user as string);
      }
    } catch (error) {
      LOG.error("Error on refunding withdraw", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getWalletStats: async () => {
    set({ isWalletStatsLoading: true });
    try {
      const response = await axios.get(`withdraws/wallet-stats`);
      if (response?.data) {
        set({ walletStats: response.data });
      }
    } catch (error) {
      LOG.error("Error on getting wallet stats", { error });
    } finally {
      set({ isWalletStatsLoading: false });
    }
  },
  downloadWalletReport: async (fromYMD: string, toYMD: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`withdraws/export/wallet`, {
        params: { from: fromYMD, to: toYMD },
      });
      const data = response?.data?.data as
        | {
            daily: {
              date: string;
              walletPositionCents: number;
              payoutToCustomerCents: number;
              /** Every withdraw customers made that day, donations included and
               *  store admins excluded. Wider than payoutToCustomerCents, which
               *  counts only what was actually paid out (Tikkie + Tomra). The
               *  endpoint has always returned this; it just was not exported. */
              outflowsCents: number;
            }[];
            monthly: {
              month: string;
              storeAdminWithdrawalsCents: number;
            }[];
          }
        | undefined;
      if (!data) {
        throw new Error("Wallet export is unavailable. Please try again.");
      }

      const delimiter = ";";
      const escape = (value: string | number) => {
        const s = String(value ?? "");
        if (s.includes('"') || s.includes("\n") || s.includes(delimiter)) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };
      const cents = (n: number) => (Number(n || 0) / 100).toFixed(2);
      // Convert backend ISO formats to dd-mm-yyyy / mm-yyyy for the CSV.
      const formatDay = (iso: string): string => {
        const [y, m, d] = (iso ?? "").split("-");
        return y && m && d ? `${d}-${m}-${y}` : iso ?? "";
      };
      const formatMonth = (iso: string): string => {
        const [y, m] = (iso ?? "").split("-");
        return y && m ? `${m}-${y}` : iso ?? "";
      };
      // Every row is padded to the same column count so the spreadsheet
      // doesn't shift the daily/monthly headers out of alignment.
      const COLS = 5;
      const row = (cells: (string | number)[]): string => {
        const padded = [...cells];
        while (padded.length < COLS) {
          padded.push("");
        }
        return padded.slice(0, COLS).map(escape).join(delimiter);
      };

      const lines: string[] = [];
      // Legend first. The two payout columns differ only by donations, which
      // is not something the reader can tell from the headers, and the daily
      // and monthly sections cover different populations - both were asked
      // about once the report started being used.
      lines.push(row(["Legend"]));
      lines.push(
        row([
          "wallet_position_eur = balance still held in customer wallets at the end of that day",
        ]),
      );
      lines.push(
        row([
          "payout_to_customer_eur = paid out to customers (Tikkie + Tomra), donations excluded",
        ]),
      );
      lines.push(
        row([
          "customer_withdrawals_eur = every withdrawal customers made, donations included",
        ]),
      );
      lines.push(
        row([
          "Daily rows cover customers only. Store-admin accounts are in the monthly section.",
        ]),
      );
      lines.push(row([]));
      // Section 1: daily wallet position + customer payouts. The two trailing
      // empty columns reserve space for the monthly section (so headers line
      // up at the same column boundaries).
      lines.push(row(["Wallet position & payouts (daily)"]));
      lines.push(
        row([
          "date",
          "wallet_position_eur",
          "payout_to_customer_eur",
          "customer_withdrawals_eur",
        ]),
      );
      for (const r of data.daily) {
        lines.push(
          row([
            formatDay(r.date),
            cents(r.walletPositionCents),
            cents(r.payoutToCustomerCents),
            cents(r.outflowsCents),
          ]),
        );
      }
      lines.push(row([]));
      // Section 2: monthly store-admin withdrawals.
      lines.push(row(["Store-admin withdrawals (monthly)"]));
      lines.push(row(["month", "store_admin_withdrawals_eur"]));
      for (const r of data.monthly) {
        lines.push(
          row([formatMonth(r.month), cents(r.storeAdminWithdrawalsCents)]),
        );
      }

      // BOM + CRLF for Excel (especially Dutch/Windows Excel) — without these
      // Excel often picks the wrong encoding and the wrong delimiter.
      const BOM = "﻿";
      const csv = BOM + lines.join("\r\n") + "\r\n";
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `wallet-report-${fromYMD}_${toYMD}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      LOG.error("Error on downloading wallet report", { error });
      throw error;
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
}));
